// PHASE_1 / B1 — the leave-balance Content component (ADR-014 §2).
// Faz 8.6 / Batch A3 restyles the VISUAL layer to the novem balance language
// (NovemHR-ekranlar leave-extra.jsx LeaveBalancePanel + emp-leave.jsx balance
// cards port). The DATA layer is untouched — the SAME useKeysetQuery page from
// GET /v1/leave/balances (BUILD_PLAN §5 keyset, never OFFSET). The self view
// sends `mine=true`, so its own-balance boundary is enforced by the server even
// when the caller also has manager-subtree scope.
//
// ONE Content, route-resolved surface variant (the A2 pattern, ADR-014 §2 —
// visual-layer branching only): /leave/balances renders the admin tenant
// table (KPI strip + filter row + level pills + usage meter), /me/leave/balance
// renders the self balance-card grid (emp-leave.jsx).
//
// Mockup elements with no backend are SKIPPED, not faked (port rule 8):
// kıdem/devamsızlık columns, department filter, sortable headers, the footer
// totals strip, the Bakiye PDF / toplu güncelle actions, the holiday side card.
// Hak ediş / kullanılan / bekleyen / devreden DO come from the API now (E35) and
// are itemised on the self cards; the admin table stays one row per balance.
// Employee/policy display names are NO LONGER stand-ins — SEM-301 + SEM-723
// added the joins and the cells render the real names (short id = fallback).
//
// Balances are STORED and edited in hours (`_hours`, decimals, never money —
// nothing here goes through <Money>) but READ in working days (E12): every
// display goes through features/leave/leave-days, and the adjust/create modals
// keep hours because that is the unit the API accepts.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, Link as RouterLink } from "react-router";
import {
  CalendarCheck2,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  Edit2,
  Plus,
  Rows3,
  Search,
  Sparkles,
  TriangleAlert,
} from "lucide-react";

import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  DataTable,
  Empty,
  ErrorState,
  FormField,
  HelpHint,
  Input,
  Loading,
  MeterBar,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PageHeader,
  Select,
  StatCard,
  type BadgeTone,
  type DataTableColumn,
} from "@novemai/components";
import {
  CALENDAR_ACCRUAL_PERIODS,
  HOURS_PER_WORKING_DAY,
  isStatutoryAnnualLeaveType,
  type CalendarAccrualPeriod,
} from "@novemai/time-attendance-sdk";

import { errorMessage } from "@/api/errors";
import { useKeysetQuery, type KeysetQueryResult } from "@/api/keyset";
import {
  bulkCreateLeaveBalances,
  createLeaveBalance,
  fetchLeaveBalancesPage,
  fetchLeaveTypes,
  patchLeaveBalance,
  type BulkCreateBalancesInput,
  type CreateBalanceInput,
  type LeaveBalanceRow,
  type LeaveUpcomingBurn,
  type PatchBalanceInput,
} from "@/features/leave/leave-api";
import { fetchEmployeesFirstPage } from "@/features/employees/api";
import { LeaveSpanText, formatLeaveSpan } from "@/features/leave/leave-days";
import { UpcomingHolidays } from "@/features/leave/upcoming-holidays";
import { formatCountWithMore } from "@/lib/count";
import { formatDate } from "@/lib/date-range";
import { PersonLink } from "@/components/person-link";
import { useDebouncedSearchParam, useFilterParam } from "@/lib/use-url-filters";

// ---- shared helpers --------------------------------------------------------

// Remaining-balance level — the mockup's kalanStyle thresholds (<5 gün Kritik,
// <10 gün Az). Balances are stored in hours, so the day thresholds are converted
// at the platform's one working day (owner decision K4) — the same 7.5 h the rest
// of this screen prints a day as. At the old 8 h figure a balance showing "5 d 2 h"
// could still read Critical. Display tone only; no business rule lives in the
// frontend.
const CRITICAL_HOURS = 5 * HOURS_PER_WORKING_DAY;
const LOW_HOURS = 10 * HOURS_PER_WORKING_DAY;

type BalanceLevel = "critical" | "low" | "normal";

function levelOf(hours: number): BalanceLevel {
  if (hours < CRITICAL_HOURS) return "critical";
  if (hours < LOW_HOURS) return "low";
  return "normal";
}

// A remaining figure only means "running low" for a balance that was ever
// something to run out of. LV-02 opens a 0 h row for every employee on unpaid leave
// and on annual leave before the first anniversary; rated by hours alone those read
// Critical, filled the "low" count and dragged the average down for every person in
// the tenant. The two facts that tell a fresh 0 from a used-up one come from the
// server (`draws_balance`, `entitlement_granted`) because the hours cannot say.
// Absent (an older server, a write endpoint's echo) means what those rows always
// were: a balance to rate.
type BalanceState = BalanceLevel | "no_draw" | "not_granted";

function stateOf(row: LeaveBalanceRow): BalanceState {
  if (row.draws_balance === false) return "no_draw";
  if (row.entitlement_granted === false) return "not_granted";
  return levelOf(Number(row.balance_hours));
}

const isRated = (state: BalanceState): state is BalanceLevel =>
  state !== "no_draw" && state !== "not_granted";
const isShortage = (state: BalanceState): boolean => state === "critical" || state === "low";

const LEVEL_TONE: Readonly<Record<BalanceState, BadgeTone>> = {
  critical: "danger",
  low: "warning",
  normal: "success",
  no_draw: "neutral",
  not_granted: "neutral",
};

const BALANCE_STATUS_TONE: Readonly<Record<string, BadgeTone>> = {
  active: "success",
  suspended: "warning",
  closed: "neutral",
};

// The short-id rendering convention the dashboard + A1/A2 established for raw
// UUIDs. Now only a FALLBACK: SEM-301/SEM-723 resolve both display names, so
// this renders when the join returns null (unresolved/redacted).
const shortId = (id: string): string => id.slice(0, 8);


function useBalancesQuery(mine = false): KeysetQueryResult<LeaveBalanceRow> {
  return useKeysetQuery<LeaveBalanceRow>({
    // A manager may visit the management and self surfaces in one session.
    // Keep their distinct server scopes out of the same React Query cache.
    queryKey: ["leave", "balances", mine ? "mine" : "management"],
    fetchPage: (cursor) => fetchLeaveBalancesPage(cursor, mine),
  });
}

// Remaining-of-cap meter (leave-extra.jsx Kullanım bar — ours fills with the
// REMAINING share since the API carries balance+cap, not used-hours). The
// column is therefore headed "Kalan / üst sınır", NOT "Kullanım": cap_hours is
// an accrual ceiling, so cap-balance is headroom rather than hours taken, and
// naming this bar "usage" inverted its meaning for every reader.
function CapMeter({
  balance,
  cap,
  caption,
  showCaption = true,
}: {
  readonly balance: number;
  readonly cap: number;
  readonly caption: string;
  /**
   * The self card already states "12 gün / 21 gün kaldı" one line above, so its
   * meter repeats the same two numbers. Hide the visible copy there — the
   * caption still reaches assistive tech through MeterBar's label. The admin
   * table keeps it: there the caption is the meter's only text.
   */
  readonly showCaption?: boolean;
}): React.ReactElement {
  const ratio = cap > 0 ? balance / cap : 0;
  return (
    <div className="min-w-[90px]">
      <MeterBar ratio={ratio} tone="accent" label={caption} className="mb-[3px]" />
      {showCaption && (
        <div className="font-sans text-[12px] text-[var(--fg-muted)]">{caption}</div>
      )}
    </div>
  );
}

// Says once, in the reader's language, what a "day" means on this screen and why
// some leave never shows up here. The figure is the SDK constant the server draws
// every request from — there is no per-tenant day length to show instead (see
// features/leave/leave-days). The second line answers the question a missing row
// raises: unpaid leave draws no balance and report/maternity leave is paid by SGK,
// so an employee who took either would otherwise look like they took nothing.
function BalanceBasisNote(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="space-y-1 font-sans text-[12px] text-[var(--fg-muted)]">
      <p>{t("leave.balance.day_basis_note", { hours: HOURS_PER_WORKING_DAY })}</p>
      <p>{t("leave.balance.payroll_note")}</p>
    </div>
  );
}

// One line of the self card's breakdown. `<dt>`/`<dd>` so the arithmetic reads
// as label/value pairs to assistive tech, not as a run of loose spans.
function BreakdownRow({
  label,
  hours,
  help,
  nested = false,
  total = false,
}: {
  readonly label: string;
  readonly hours: string;
  readonly help?: React.ReactNode;
  /** A part of the row above it (carryover sits inside the entitlement). */
  readonly nested?: boolean;
  /** The line the rows above add up to. */
  readonly total?: boolean;
}): React.ReactElement {
  return (
    <div
      className={`flex items-baseline justify-between gap-3 ${nested ? "pl-3" : ""} ${
        total ? "mt-1 border-t border-[var(--border-subtle)] pt-1.5 font-medium" : ""
      }`}
    >
      <dt className="inline-flex items-center gap-1 text-[var(--fg-muted)]">
        {label}
        {help}
      </dt>
      <dd className="m-0 text-right text-[var(--fg-secondary)]">
        <LeaveSpanText hours={hours} />
      </dd>
    </div>
  );
}

// "X yanacak" — the balance's forecast of contract-derived carryover that lapses
// if left unused. Statutory days never burn (owner decision K2), so most cards
// have nothing to say here and render NOTHING: no line beats a "0 saat" one that
// would read as a warning about a balance that is safe. The server sends the
// dates nearest-first; the nearest is the line and the rest sit behind a native
// <details> — a tooltip cannot hold a list a keyboard user can open and read.
function UpcomingBurnLine({
  burns,
}: {
  readonly burns: readonly LeaveUpcomingBurn[];
}): React.ReactElement | null {
  const { t, i18n } = useTranslation();
  const [next, ...later] = burns;
  if (next === undefined) return null;
  const span = (hours: string): string =>
    formatLeaveSpan(hours, i18n.language, t) ?? t("common.unknown");
  const day = (iso: string): string => formatDate(i18n.language, iso);
  return (
    <div
      data-testid="balance-burn-line"
      className="mt-3 rounded-lg bg-[color-mix(in_srgb,var(--state-warning)_8%,var(--bg-raised))] px-3 py-2 font-sans text-[12.5px] text-[var(--fg-primary)]"
    >
      <div className="flex items-start gap-2">
        <CalendarClock
          size={14}
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-[var(--state-warning)]"
        />
        <p className="m-0 flex-1">
          {t("leave.balance.burn_line", {
            span: span(next.hours),
            date: day(next.expires_on),
          })}
        </p>
        <HelpHint label={t("common.help_for", { field: t("leave.balance.burn_title") })}>
          {t("leave.balance.burn_help")}
        </HelpHint>
      </div>
      {later.length > 0 && (
        <details className="mt-1.5 pl-[22px]">
          <summary className="cursor-pointer text-[var(--fg-muted)]">
            {t("leave.balance.burn_more", { n: later.length })}
          </summary>
          <ul className="m-0 mt-1 list-none space-y-0.5 p-0 text-[var(--fg-secondary)]">
            {later.map((burn) => (
              <li key={burn.expires_on}>
                {t("leave.balance.burn_later_row", {
                  date: day(burn.expires_on),
                  span: span(burn.hours),
                })}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ---- dispatcher (ADR-014: ONE Content, route-resolved surface variant) -----

export function LeaveBalanceContent(): React.ReactElement {
  const { pathname } = useLocation();
  if (pathname.startsWith("/me/leave/balance")) return <MyBalanceView />;
  return <AdminBalanceView />;
}

// ============================================================================
// /leave/balances — yönetici bakiye tablosu (leave-extra.jsx LeaveBalancePanel)
// ============================================================================

function AdminBalanceView(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  // Both narrows are in the URL (SEM-6350): the rows link to a person now, and
  // returning from one must not drop the filter that found them.
  const [search, setSearch] = useDebouncedSearchParam("q");
  const [lowRaw, setLowRaw] = useFilterParam("low");
  const lowOnly = lowRaw === "1";
  const setLowOnly = (next: boolean): void => setLowRaw(next ? "1" : "");

  const [createSingleOpen, setCreateSingleOpen] = useState(false);
  const [createBulkOpen, setCreateBulkOpen] = useState(false);
  const [adjustingRow, setAdjustingRow] = useState<LeaveBalanceRow | null>(null);
  const [bannerMessage, setBannerMessage] = useState<string | null>(null);

  const query = useBalancesQuery();

  const fmtSpan = (hours: string | number): string =>
    formatLeaveSpan(hours, i18n.language, t) ?? t("common.unknown");

  // JSX satırındaki "> 0 ?" karşılaştırması i18n tarayıcısında metin sanılıyor.
  const isPositiveHours = (hours: string | number): boolean => Number(hours) > 0;

  // The breakdown fields are optional on the row (a write endpoint echoes a
  // balance without them); an absent figure prints as a dash, never as 0.
  const breakdownCell = (
    row: LeaveBalanceRow,
    hours: string | null | undefined,
    render: (hours: string) => React.ReactNode,
  ): React.ReactNode => {
    if (hours === undefined || hours === null || stateOf(row) === "no_draw") {
      return <span className="text-[13px] text-[var(--fg-muted)]">—</span>;
    }
    return render(hours);
  };
  const plainSpan = (hours: string): React.ReactNode => (
    <span className="whitespace-nowrap text-[13px] text-[var(--fg-secondary)]">
      {fmtSpan(hours)}
    </span>
  );

  // Honest aggregates over the loaded window ("+" while hasNextPage signals
  // further pages — the dashboard/A1 convention).
  const totalHours = query.items.reduce((s, r) => s + Number(r.balance_hours), 0);
  const lowCount = query.items.filter((r) => isShortage(stateOf(r))).length;
  // The average is over balances that are rated: a person's unpaid-leave row or a
  // first-year 0 is not a balance of 0 days, and counting it halves the figure the
  // tile is there to show.
  const ratedItems = query.items.filter((r) => isRated(stateOf(r)));
  const avgHours =
    ratedItems.length > 0
      ? ratedItems.reduce((s, r) => s + Number(r.balance_hours), 0) / ratedItems.length
      : 0;

  const q = search.trim().toLowerCase();
  const rows = query.items.filter((r) => {
    if (lowOnly && !isShortage(stateOf(r))) return false;
    if (q === "") return true;
    return (
      (r.employee_name ?? "").toLowerCase().includes(q) ||
      (r.policy_name ?? "").toLowerCase().includes(q) ||
      r.employee_id.toLowerCase().includes(q) ||
      r.policy_id.toLowerCase().includes(q)
    );
  });

  const columns: readonly DataTableColumn<LeaveBalanceRow>[] = [
    {
      id: "employee",
      header: t("leave.balance.column_employee"),
      cell: (row): React.ReactNode => (
        <div className="flex items-center gap-2.5">
          <Avatar size={32} alt={row.employee_name ?? shortId(row.employee_id)} />
          <PersonLink employeeId={row.employee_id}>
            <span className="whitespace-nowrap font-serif text-[14.5px] text-[var(--fg-primary)]">
              {row.employee_name ?? shortId(row.employee_id)}
            </span>
          </PersonLink>
        </div>
      ),
    },
    {
      id: "policy",
      header: t("leave.balance.column.policy"),
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap font-sans text-[12px] text-[var(--fg-muted)]">
          {row.policy_name ?? shortId(row.policy_id)}
        </span>
      ),
    },
    {
      id: "entitlement",
      header: t("leave.balance.breakdown_entitlement"),
      cell: (row): React.ReactNode =>
        breakdownCell(row, row.entitlement_hours, (hours) => (
          <span className="whitespace-nowrap text-[13px] font-semibold text-[var(--fg-primary)]">
            {fmtSpan(hours)}
          </span>
        )),
    },
    {
      id: "used",
      header: t("leave.balance.breakdown_used"),
      cell: (row): React.ReactNode => breakdownCell(row, row.used_hours, plainSpan),
    },
    {
      id: "balance",
      header: (
        <span className="inline-flex items-center gap-1">
          {t("leave.balance.column.balance_hours")}
          <HelpHint
            label={t("common.help_for", {
              field: t("leave.balance.column.balance_hours"),
            })}
          >
            {t("leave.balance.level.critical_help")}
          </HelpHint>
        </span>
      ),
      cell: (row): React.ReactNode => {
        const state = stateOf(row);
        // No figure for a type that draws no balance: "0 days" there answers a
        // question nobody asked.
        if (state === "no_draw") {
          return <Badge tone={LEVEL_TONE[state]}>{t(`leave.balance.level.${state}`)}</Badge>;
        }
        return (
          <Badge tone={LEVEL_TONE[state]}>
            {fmtSpan(row.balance_hours)}
            <span className="text-[10px] opacity-75">
              {t(`leave.balance.level.${state}`)}
            </span>
          </Badge>
        );
      },
    },
    {
      id: "carryover",
      header: t("leave.balance.breakdown_carryover"),
      // Already part of the entitlement figure (the header says so): shown
      // apart, never as an addend.
      cell: (row): React.ReactNode =>
        breakdownCell(row, row.carryover_hours, (hours) =>
          isPositiveHours(hours) ? <Badge tone="info">{fmtSpan(hours)}</Badge> : plainSpan(hours),
        ),
    },
    {
      id: "pending",
      header: t("leave.balance.breakdown_pending"),
      cell: (row): React.ReactNode =>
        breakdownCell(row, row.pending_hours, (hours) =>
          isPositiveHours(hours) ? <Badge tone="warning">{fmtSpan(hours)}</Badge> : plainSpan(hours),
        ),
    },
    {
      id: "cap",
      header: t("leave.balance.column.cap_hours"),
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap text-[13px] text-[var(--fg-secondary)]">
          {row.cap_hours !== null ? (
            <LeaveSpanText hours={row.cap_hours} />
          ) : (
            t("leave.balance.cell.no_cap")
          )}
        </span>
      ),
    },
    {
      id: "usage",
      header: t("leave.balance.column_remaining_of_cap"),
      cell: (row): React.ReactNode =>
        row.cap_hours !== null ? (
          <CapMeter
            balance={Number(row.balance_hours)}
            cap={Number(row.cap_hours)}
            caption={t("leave.balance.remaining_of_cap", {
              balance: fmtSpan(row.balance_hours),
              cap: fmtSpan(row.cap_hours),
            })}
          />
        ) : (
          <span className="text-[13px] text-[var(--fg-muted)]">
            {t("leave.balance.cell.no_cap")}
          </span>
        ),
    },
    {
      id: "status",
      header: t("leave.balance.column.status"),
      cell: (row): React.ReactNode => (
        <Badge tone={BALANCE_STATUS_TONE[row.status] ?? "neutral"}>
          {t(`leave.balance.status.${row.status}`)}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: (row): React.ReactNode => (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setAdjustingRow(row)}
          >
            <Edit2 size={13} aria-hidden="true" />
            {t("leave.balance_actions.adjust")}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-[1320px] space-y-6">
      <PageHeader
        title={t("leave.balance.title")}
        description={t("leave.balance.lead", {
          total: formatCountWithMore(query.items.length, query.hasNextPage),
          low: lowCount,
        })}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setCreateBulkOpen(true)}
            >
              <Sparkles size={14} aria-hidden="true" />
              {t("leave.balance_actions.create_bulk")}
            </Button>
            <Button
              variant="primary"
              onClick={() => setCreateSingleOpen(true)}
            >
              <Plus size={14} aria-hidden="true" />
              {t("leave.balance_actions.create_single")}
            </Button>
          </div>
        }
      />

      {bannerMessage && (
        <div className="flex items-center justify-between rounded-lg border border-[var(--state-success)]/30 bg-[color-mix(in_srgb,var(--state-success)_8%,var(--bg-raised))] p-3 text-[13px] text-[var(--state-success)]">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} />
            <span>{bannerMessage}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setBannerMessage(null)}>
            ✕
          </Button>
        </div>
      )}

      {/* KPI strip — leave-extra.jsx kpis */}
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <StatCard
          label={t("leave.balance.kpi_total_hours")}
          value={fmtSpan(totalHours)}
          tone="success"
          icon={<CalendarCheck2 size={18} />}
        />
        <StatCard
          label={t("leave.balance.kpi_avg_hours")}
          value={fmtSpan(avgHours)}
          tone="info"
          icon={<CalendarDays size={18} />}
        />
        <StatCard
          label={t("leave.balance.kpi_low")}
          value={formatCountWithMore(lowCount, query.hasNextPage)}
          tone="danger"
          icon={<TriangleAlert size={18} />}
        />
        <StatCard
          label={t("leave.balance.kpi_records")}
          value={formatCountWithMore(query.items.length, query.hasNextPage)}
          tone="accent"
          icon={<Rows3 size={18} />}
        />
      </div>

      {query.isLoading ? (
        <Loading label={t("leave.balance.loading")} rows={5} />
      ) : query.isError ? (
        <ErrorState
          title={t("leave.balance.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : query.items.length === 0 ? (
        <Empty
          title={t("leave.balance.empty_title")}
          description={t("leave.balance.empty_description")}
        />
      ) : (
        <div className="space-y-3">
          {/* Filter row — leave-extra.jsx Filtreler (loaded-window client-side;
              server-side filters are a recorded follow-up, the A1/A2 stance) */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-64">
              <Search
                size={14}
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-muted)]"
              />
              <Input
                aria-label={t("leave.balance.search_label")}
                placeholder={t("leave.balance.search_placeholder")}
                value={search}
                onChange={(e): void => {
                  setSearch(e.target.value);
                }}
                className="nai-input-search"
              />
            </div>
            <Button
              variant={lowOnly ? "primary" : "secondary"}
              size="sm"
              aria-pressed={lowOnly}
              onClick={(): void => {
                setLowOnly(!lowOnly);
              }}
            >
              <TriangleAlert size={13} aria-hidden="true" />
              {t("leave.balance.low_filter")}
            </Button>
            <span className="ml-auto font-sans text-[12.5px] text-[var(--fg-muted)]">
              {t("leave.balance.filter_count", {
                shown: rows.length,
                total: formatCountWithMore(query.items.length, query.hasNextPage),
              })}
            </span>
          </div>

          {rows.length === 0 ? (
            <>
              <Empty
                title={t("leave.balance.empty_filtered_title")}
                description={t("leave.balance.empty_filtered_description")}
              />
              {query.hasNextPage && (
                <div className="flex justify-center">
                  <Button
                    variant="secondary"
                    onClick={query.fetchNextPage}
                    loading={query.isFetchingNextPage}
                  >
                    {t("leave.balance.load_more")}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Card className="overflow-hidden">
              <DataTable<LeaveBalanceRow>
                caption={t("leave.balance.table_caption")}
                columns={columns}
                rows={rows}
                rowKey={(row): string => row.balance_id}
                hasNextPage={query.hasNextPage}
                onLoadMore={query.fetchNextPage}
                isFetchingNextPage={query.isFetchingNextPage}
                loadMoreLabel={t("leave.balance.load_more")}
              />
            </Card>
          )}
          <BalanceBasisNote />
        </div>
      )}

      {createSingleOpen && (
        <CreateBalanceModal
          onClose={() => setCreateSingleOpen(false)}
          onSuccess={(msg) => {
            setCreateSingleOpen(false);
            setBannerMessage(msg);
            queryClient.invalidateQueries({ queryKey: ["leave", "balances"] });
          }}
        />
      )}

      {createBulkOpen && (
        <BulkCreateBalancesModal
          onClose={() => setCreateBulkOpen(false)}
          onSuccess={(msg) => {
            setCreateBulkOpen(false);
            setBannerMessage(msg);
            queryClient.invalidateQueries({ queryKey: ["leave", "balances"] });
          }}
        />
      )}

      {adjustingRow && (
        <AdjustBalanceModal
          row={adjustingRow}
          onClose={() => setAdjustingRow(null)}
          onSuccess={(msg) => {
            setAdjustingRow(null);
            setBannerMessage(msg);
            queryClient.invalidateQueries({ queryKey: ["leave", "balances"] });
          }}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// CreateBalanceModal (Single Employee)
// ----------------------------------------------------------------------------

function CreateBalanceModal({
  onClose,
  onSuccess,
}: {
  readonly onClose: () => void;
  readonly onSuccess: (msg: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();

  const [employeeId, setEmployeeId] = useState("");
  const [policyId, setPolicyId] = useState("");
  // Varsayılan 0: yasal izin yıldönümünde kıdem ve yaşa göre verilir; herkese aynı saati yazmak ikisini yok sayar (sunucu toplu yasal açılışta 0 dışını reddeder).
  const [balanceHours, setBalanceHours] = useState("0");
  const [capHours, setCapHours] = useState("");
  const [carryoverHours, setCarryoverHours] = useState("");
  const [accrualRate, setAccrualRate] = useState("");
  const [accrualPeriod, setAccrualPeriod] = useState<CalendarAccrualPeriod | "">("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const employeesQuery = useQuery({
    queryKey: ["employees", "first-page"],
    queryFn: fetchEmployeesFirstPage,
  });

  const leaveTypesQuery = useQuery({
    queryKey: ["leave", "types", "active"],
    queryFn: () => fetchLeaveTypes("active"),
  });

  const selectedType = (leaveTypesQuery.data ?? []).find((lt) => lt.platform_policy_id === policyId);
  // Statutory and anniversary balances are granted by the entitlement sweep, and their
  // rows are opened for every employee up front: the route answers a second one with
  // 409 and rejects a rate on either (LV-01, LV-02). Saying so here beats a 409 the
  // admin has to decode.
  const grantedOnAnniversary =
    selectedType !== undefined &&
    (isStatutoryAnnualLeaveType({
      accrualKind: selectedType.accrual_kind,
      statutoryRef: selectedType.statutory_ref,
    }) ||
      selectedType.accrual_kind === "anniversary");
  const unpaidType = selectedType !== undefined && !selectedType.paid;

  const employeeOptions = (employeesQuery.data?.items ?? []).map((emp) => ({
    value: emp.employee_id,
    label: `${emp.legal_first_name} ${emp.legal_last_name} (${emp.primary_work_email ?? emp.employee_id.slice(0, 8)})`,
  }));

  const policyOptions = (leaveTypesQuery.data ?? []).map((lt) => ({
    value: lt.platform_policy_id,
    label: `${lt.name} (${lt.code})`,
  }));

  const mutation = useMutation({
    mutationFn: async () => {
      // Empty fields are OMITTED, not sent as null: the route reads null as invalid.
      const input: CreateBalanceInput = {
        employee_id: employeeId,
        policy_id: policyId,
        balance_hours: Number(balanceHours),
        ...(capHours.trim() !== "" && { cap_hours: Number(capHours) }),
        ...(carryoverHours.trim() !== "" && { carryover_hours: Number(carryoverHours) }),
        ...(!grantedOnAnniversary &&
          accrualRate.trim() !== "" && { accrual_rate_hours_per_period: Number(accrualRate) }),
        ...(!grantedOnAnniversary && accrualPeriod !== "" && { accrual_period: accrualPeriod }),
        ...(notes.trim() !== "" && { notes: notes.trim() }),
      };
      return createLeaveBalance(input, crypto.randomUUID());
    },
    onSuccess: () => {
      onSuccess(t("leave.balance_actions.success_create_single"));
    },
    onError: (err) => {
      setFormError(errorMessage(err, t));
    },
  });

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    if (!employeeId) {
      setFormError(t("leave.balance_actions.select_employee"));
      return;
    }
    if (!policyId) {
      setFormError(t("leave.balance_actions.select_policy"));
      return;
    }
    if (isNaN(Number(balanceHours)) || Number(balanceHours) < 0) {
      setFormError(t("leave.balance_actions.field_balance_hours"));
      return;
    }
    // A rate without a period (or the reverse) is accepted by the route and then never
    // accrues: no schedule is armed. Ask for both instead of saving a dead setting.
    if (!grantedOnAnniversary && (accrualRate.trim() === "") !== (accrualPeriod === "")) {
      setFormError(t("leave.balance_actions.accrual_pair_required"));
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal open onClose={onClose}>
      <ModalHeader title={t("leave.balance_actions.create_single_title")} />
      <form onSubmit={handleSubmit}>
        <ModalBody className="space-y-4 max-h-[70vh] overflow-y-auto">
          {formError && (
            <div className="rounded border border-[var(--state-danger)]/30 bg-[color-mix(in_srgb,var(--state-danger)_8%,var(--bg-raised))] p-2.5 text-[12.5px] text-[var(--state-danger)]">
              {formError}
            </div>
          )}

          <FormField label={t("leave.balance_actions.select_employee")} required>
            {employeeOptions.length > 0 ? (
              <Select
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
              >
                <option value="">—</option>
                {employeeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                placeholder={t("leave.balance_actions.placeholder_employee_id")}
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                required
              />
            )}
          </FormField>

          <FormField label={t("leave.balance_actions.select_policy")} required>
            {policyOptions.length > 0 ? (
              <Select
                value={policyId}
                onChange={(e) => setPolicyId(e.target.value)}
              >
                <option value="">—</option>
                {policyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                placeholder={t("leave.balance_actions.placeholder_policy_id")}
                value={policyId}
                onChange={(e) => setPolicyId(e.target.value)}
                required
              />
            )}
          </FormField>

          {grantedOnAnniversary && (
            <p className="text-[12.5px] text-[var(--fg-muted)]">
              {t("leave.balance_actions.hint_auto_opened")}
            </p>
          )}
          {unpaidType && (
            <p className="text-[12.5px] text-[var(--fg-muted)]">
              {t("leave.balance_actions.hint_unpaid_no_balance")}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <FormField label={t("leave.balance_actions.field_balance_hours")} required>
              <Input
                type="number"
                step="0.5"
                value={balanceHours}
                onChange={(e) => setBalanceHours(e.target.value)}
                required
              />
            </FormField>

            <FormField label={t("leave.balance_actions.field_cap_hours")}>
              <Input
                type="number"
                step="0.5"
                placeholder={t("leave.balance_actions.placeholder_cap_hours")}
                value={capHours}
                onChange={(e) => setCapHours(e.target.value)}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label={t("leave.balance_actions.field_carryover_hours")}>
              <Input
                type="number"
                step="0.5"
                placeholder={t("leave.balance_actions.placeholder_carryover_hours")}
                value={carryoverHours}
                onChange={(e) => setCarryoverHours(e.target.value)}
              />
            </FormField>

            <FormField
              label={t("leave.balance_actions.field_accrual_rate")}
              hint={t("leave.balance_actions.hint_accrual_rate")}
            >
              <Input
                type="number"
                step="0.5"
                placeholder={t("leave.balance_actions.placeholder_accrual_rate")}
                value={grantedOnAnniversary ? "" : accrualRate}
                disabled={grantedOnAnniversary}
                onChange={(e) => setAccrualRate(e.target.value)}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label={t("leave.balance_actions.field_accrual_period")}>
              <Select
                value={grantedOnAnniversary ? "" : accrualPeriod}
                disabled={grantedOnAnniversary}
                onChange={(e) => setAccrualPeriod(e.target.value as CalendarAccrualPeriod | "")}
              >
                <option value="">—</option>
                {CALENDAR_ACCRUAL_PERIODS.map((period) => (
                  <option key={period} value={period}>
                    {t(`leave.balance_actions.period_${period}`)}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField label={t("leave.balance_actions.field_notes")}>
              <Input
                placeholder={t("leave.balance_actions.placeholder_notes_single")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </FormField>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            {t("common.cancel")}
          </Button>
          <Button variant="primary" type="submit" loading={mutation.isPending}>
            {t("leave.balance_actions.submit_create")}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// BulkCreateBalancesModal
// ----------------------------------------------------------------------------

function BulkCreateBalancesModal({
  onClose,
  onSuccess,
}: {
  readonly onClose: () => void;
  readonly onSuccess: (msg: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();

  const [policyId, setPolicyId] = useState("");
  const [scopeKind, setScopeKind] = useState<"all" | "department" | "location" | "legal_entity">("all");
  const [scopeId, setScopeId] = useState("");
  // Varsayılan 0: yasal izin yıldönümünde kıdem ve yaşa göre verilir; herkese aynı saati yazmak ikisini yok sayar (sunucu toplu yasal açılışta 0 dışını reddeder).
  const [balanceHours, setBalanceHours] = useState("0");
  const [capHours, setCapHours] = useState("");
  const [carryoverHours, setCarryoverHours] = useState("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const leaveTypesQuery = useQuery({
    queryKey: ["leave", "types", "active"],
    queryFn: () => fetchLeaveTypes("active"),
  });

  const policyOptions = (leaveTypesQuery.data ?? []).map((lt) => ({
    value: lt.platform_policy_id,
    label: `${lt.name} (${lt.code})`,
  }));

  const selectedType = (leaveTypesQuery.data ?? []).find((lt) => lt.platform_policy_id === policyId);
  const statutoryType =
    selectedType !== undefined &&
    isStatutoryAnnualLeaveType({
      accrualKind: selectedType.accrual_kind,
      statutoryRef: selectedType.statutory_ref,
    });
  const unpaidType = selectedType !== undefined && !selectedType.paid;

  const mutation = useMutation({
    mutationFn: async () => {
      // Empty fields are OMITTED, not sent as null: the route reads null as invalid,
      // and the default "all employees" scope has no scope_id to send at all.
      const input: BulkCreateBalancesInput = {
        policy_id: policyId,
        scope_kind: scopeKind,
        ...(scopeKind !== "all" && scopeId.trim() !== "" && { scope_id: scopeId.trim() }),
        // The route refuses any hours on a statutory type (LV-01): the field shows 0.
        balance_hours: statutoryType ? 0 : Number(balanceHours),
        ...(capHours.trim() !== "" && { cap_hours: Number(capHours) }),
        ...(carryoverHours.trim() !== "" && { carryover_hours: Number(carryoverHours) }),
        ...(notes.trim() !== "" && { notes: notes.trim() }),
      };
      return bulkCreateLeaveBalances(input, crypto.randomUUID());
    },
    onSuccess: (res) => {
      onSuccess(
        res.affected_count === 0
          ? t("leave.balance_actions.success_bulk_none")
          : t("leave.balance_actions.success_bulk", { count: res.affected_count }),
      );
    },
    onError: (err) => {
      setFormError(errorMessage(err, t));
    },
  });

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    if (!policyId) {
      setFormError(t("leave.balance_actions.select_policy"));
      return;
    }
    if (isNaN(Number(balanceHours)) || Number(balanceHours) < 0) {
      setFormError(t("leave.balance_actions.field_balance_hours"));
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal open onClose={onClose}>
      <ModalHeader title={t("leave.balance_actions.create_bulk_title")} />
      <form onSubmit={handleSubmit}>
        <ModalBody className="space-y-4 max-h-[70vh] overflow-y-auto">
          {formError && (
            <div className="rounded border border-[var(--state-danger)]/30 bg-[color-mix(in_srgb,var(--state-danger)_8%,var(--bg-raised))] p-2.5 text-[12.5px] text-[var(--state-danger)]">
              {formError}
            </div>
          )}

          <FormField label={t("leave.balance_actions.select_policy")} required>
            {policyOptions.length > 0 ? (
              <Select
                value={policyId}
                onChange={(e) => setPolicyId(e.target.value)}
              >
                <option value="">—</option>
                {policyOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                placeholder={t("leave.balance_actions.placeholder_policy_id")}
                value={policyId}
                onChange={(e) => setPolicyId(e.target.value)}
                required
              />
            )}
          </FormField>

          {statutoryType && (
            <p className="text-[12.5px] text-[var(--fg-muted)]">{t("leave.types.hint_statutory_bulk")}</p>
          )}
          {unpaidType && (
            <p className="text-[12.5px] text-[var(--fg-muted)]">
              {t("leave.balance_actions.hint_unpaid_no_balance")}
            </p>
          )}

          <FormField label={t("leave.types.field_scope_kind")} required>
            <Select
              value={scopeKind}
              onChange={(e) =>
                setScopeKind(e.target.value as "all" | "department" | "location" | "legal_entity")
              }
            >
              <option value="all">{t("leave.types.scope_all")}</option>
              <option value="department">{t("leave.types.scope_department")}</option>
              <option value="location">{t("leave.types.scope_location")}</option>
              <option value="legal_entity">{t("leave.types.scope_legal_entity")}</option>
            </Select>
          </FormField>

          {scopeKind !== "all" && (
            <FormField label={t("leave.types.field_scope_id")} required>
              <Input
                placeholder={t("leave.types.placeholder_scope_id")}
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
                required
              />
            </FormField>
          )}

          <div className="grid grid-cols-2 gap-3">
            <FormField label={t("leave.balance_actions.field_balance_hours")} required>
              <Input
                type="number"
                step="0.5"
                value={statutoryType ? "0" : balanceHours}
                disabled={statutoryType}
                onChange={(e) => setBalanceHours(e.target.value)}
                required
              />
            </FormField>

            <FormField label={t("leave.balance_actions.field_cap_hours")}>
              <Input
                type="number"
                step="0.5"
                placeholder={t("leave.balance_actions.placeholder_cap_hours")}
                value={capHours}
                onChange={(e) => setCapHours(e.target.value)}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label={t("leave.balance_actions.field_carryover_hours")}>
              <Input
                type="number"
                step="0.5"
                placeholder={t("leave.balance_actions.placeholder_carryover_hours")}
                value={carryoverHours}
                onChange={(e) => setCarryoverHours(e.target.value)}
              />
            </FormField>

            <FormField label={t("leave.balance_actions.field_notes")}>
              <Input
                placeholder={t("leave.balance_actions.placeholder_notes_bulk")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </FormField>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            {t("common.cancel")}
          </Button>
          <Button variant="primary" type="submit" loading={mutation.isPending}>
            {t("leave.balance_actions.submit_bulk")}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ----------------------------------------------------------------------------
// AdjustBalanceModal
// ----------------------------------------------------------------------------

function AdjustBalanceModal({
  row,
  onClose,
  onSuccess,
}: {
  readonly row: LeaveBalanceRow;
  readonly onClose: () => void;
  readonly onSuccess: (msg: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();

  const [balanceHours, setBalanceHours] = useState(row.balance_hours);
  const [capHours, setCapHours] = useState(row.cap_hours ?? "");
  const [carryoverHours, setCarryoverHours] = useState("");
  const [status, setStatus] = useState<"active" | "suspended" | "closed">(
    row.status as "active" | "suspended" | "closed",
  );
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  // Only what the admin changed goes to the route. PATCH reads `null` as "clear this
  // field", so the old body — carryover and notes are not on the list row and start
  // blank — wiped a hand-keyed carryover and note on every balance edit. The list's
  // carryover is also DERIVED from the entitlement ledger for ledger-backed
  // balances, so pre-filling it here would write a computed figure over the stored one.
  const changes = (): PatchBalanceInput => {
    const capValue = capHours.trim() !== "" ? Number(capHours) : null;
    const rowCap = row.cap_hours !== null ? Number(row.cap_hours) : null;
    return {
      ...(Number(balanceHours) !== Number(row.balance_hours) && {
        balance_hours: Number(balanceHours),
      }),
      ...(capValue !== rowCap && { cap_hours: capValue }),
      ...(carryoverHours.trim() !== "" && { carryover_hours: Number(carryoverHours) }),
      ...(status !== row.status && { status }),
      ...(notes.trim() !== "" && { notes: notes.trim() }),
    };
  };

  const mutation = useMutation({
    mutationFn: async () => patchLeaveBalance(row.balance_id, changes()),
    onSuccess: () => {
      onSuccess(t("leave.balance_actions.success_adjust"));
    },
    onError: (err) => {
      setFormError(errorMessage(err, t));
    },
  });

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    if (isNaN(Number(balanceHours)) || Number(balanceHours) < 0) {
      setFormError(t("leave.balance_actions.field_balance_hours"));
      return;
    }
    if (Object.keys(changes()).length === 0) {
      setFormError(t("leave.balance_actions.adjust_nothing_changed"));
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal open onClose={onClose}>
      <ModalHeader
        title={`${t("leave.balance_actions.adjust_title")} (${row.employee_name ?? row.employee_id} · ${row.policy_name ?? row.policy_id})`}
      />
      <form onSubmit={handleSubmit}>
        <ModalBody className="space-y-4 max-h-[70vh] overflow-y-auto">
          {formError && (
            <div className="rounded border border-[var(--state-danger)]/30 bg-[color-mix(in_srgb,var(--state-danger)_8%,var(--bg-raised))] p-2.5 text-[12.5px] text-[var(--state-danger)]">
              {formError}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <FormField label={t("leave.balance_actions.field_balance_hours")} required>
              <Input
                type="number"
                step="0.5"
                value={balanceHours}
                onChange={(e) => setBalanceHours(e.target.value)}
                required
              />
            </FormField>

            <FormField label={t("leave.balance_actions.field_cap_hours")}>
              <Input
                type="number"
                step="0.5"
                value={capHours}
                onChange={(e) => setCapHours(e.target.value)}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label={t("leave.balance_actions.field_carryover_hours")}>
              <Input
                type="number"
                step="0.5"
                value={carryoverHours}
                onChange={(e) => setCarryoverHours(e.target.value)}
              />
            </FormField>

            <FormField label={t("leave.balance_actions.field_status")} required>
              <Select
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as "active" | "suspended" | "closed")
                }
              >
                <option value="active">{t("leave.balance.status.active")}</option>
                <option value="suspended">{t("leave.balance.status.suspended")}</option>
                <option value="closed">{t("leave.balance.status.closed")}</option>
              </Select>
            </FormField>
          </div>

          <FormField label={t("leave.balance_actions.field_notes")}>
            <Input
              placeholder={t("leave.balance_actions.placeholder_notes_adjust")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </FormField>

          <p className="text-[12.5px] text-[var(--fg-muted)]">
            {t("leave.balance_actions.hint_adjust_blank_keeps")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            {t("common.cancel")}
          </Button>
          <Button variant="primary" type="submit" loading={mutation.isPending}>
            {t("leave.balance_actions.submit_adjust")}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}


// ============================================================================
// /me/leave/balance — çalışan bakiye kartları (emp-leave.jsx Bakiye kartları)
// ============================================================================

function MyBalanceView(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const fmtSpan = (hours: string | number): string =>
    formatLeaveSpan(hours, i18n.language, t) ?? t("common.unknown");
  // `mine=true` is a server-side authority boundary. Client filtering cannot
  // safely turn a manager-subtree response into a self-service response.
  const query = useBalancesQuery(true);

  return (
    <div className="mx-auto max-w-[1100px] space-y-6">
      <PageHeader
        title={t("leave.balance.title_self")}
        description={t("leave.balance.lead_self")}
        actions={
          <Button variant="primary" asChild>
            <RouterLink to="/me/leave/file">
              <Plus size={14} aria-hidden="true" />
              {t("leave.balance.request_leave")}
            </RouterLink>
          </Button>
        }
      />

      {query.isLoading ? (
        <Loading label={t("leave.balance.loading")} rows={4} />
      ) : query.isError ? (
        <ErrorState
          title={t("leave.balance.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : query.items.length === 0 ? (
        <Empty
          title={t("leave.balance.empty_title")}
          description={t("leave.balance.empty_description")}
        />
      ) : (
        <>
          <div data-tour="balance-cards" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {query.items.map((row) => {
              const balance = Number(row.balance_hours);
              const state = stateOf(row);
              // Meter, arithmetic and per-year lines describe a balance that has
              // been granted; a fresh 0 or an unpaid type has none of it to show.
              const rated = isRated(state);
              return (
                <Card key={row.balance_id}>
                  <CardBody>
                    <div className="mb-2.5 flex items-center justify-between gap-2">
                      <span className="font-serif text-[15.5px] text-[var(--fg-primary)]">
                        {t("leave.balance.card_title", {
                          policy: row.policy_name ?? shortId(row.policy_id),
                        })}
                      </span>
                      {/* No column head to hang the hint on here — the card's
                          own pill carries it. */}
                      <span className="inline-flex items-center gap-1">
                        <Badge size="sm" tone={LEVEL_TONE[state]}>
                          {t(`leave.balance.level.${state}`)}
                        </Badge>
                        <HelpHint
                          label={t("common.help_for", {
                            field: t(`leave.balance.level.${state}`),
                          })}
                        >
                          {t("leave.balance.level.critical_help")}
                        </HelpHint>
                      </span>
                    </div>
                    {state === "no_draw" ? (
                      <p className="m-0 font-sans text-[12.5px] text-[var(--fg-muted)]">
                        {t("leave.balance.no_draw_note")}
                      </p>
                    ) : (
                      <div className="flex flex-wrap items-baseline gap-x-1.5">
                        <span className="font-serif text-[32px] font-medium leading-none text-[var(--fg-primary)]">
                          {fmtSpan(row.balance_hours)}
                        </span>
                        <span className="font-sans text-[12.5px] text-[var(--fg-muted)]">
                          {row.cap_hours !== null
                            ? t("leave.balance.card_remaining_suffix", { cap: fmtSpan(row.cap_hours) })
                            : t("leave.balance.card_remaining_no_cap")}
                        </span>
                      </div>
                    )}
                    {state === "not_granted" && (
                      <p className="m-0 mt-2 font-sans text-[12.5px] text-[var(--fg-muted)]">
                        {t("leave.balance.not_granted_note")}
                      </p>
                    )}
                    {rated && row.cap_hours !== null && (
                      <div className="mt-3.5">
                        <CapMeter
                          balance={balance}
                          cap={Number(row.cap_hours)}
                          showCaption={false}
                          caption={t("leave.balance.remaining_of_cap", {
                            balance: fmtSpan(row.balance_hours),
                            cap: fmtSpan(row.cap_hours),
                          })}
                        />
                      </div>
                    )}
                    {rated && <UpcomingBurnLine burns={row.upcoming_burns ?? []} />}
                    {/* The itemised breakdown (E35): entitlement - used - pending
                        = remaining, all from the same read as the big number, so
                        the rows add up by construction. Absent when the server
                        did not send it (an older deployment) rather than guessed. */}
                    {rated &&
                      row.entitlement_hours !== undefined &&
                      row.used_hours !== undefined &&
                      row.pending_hours !== undefined && (
                        <dl
                          aria-label={t("leave.balance.breakdown_title")}
                          className="m-0 mt-3 space-y-1 border-t border-[var(--border-subtle)] pt-2.5 font-sans text-[12.5px]"
                        >
                          <BreakdownRow
                            label={t("leave.balance.breakdown_entitlement")}
                            hours={row.entitlement_hours}
                          />
                          {Number(row.carryover_hours ?? 0) > 0 && (
                            <BreakdownRow
                              nested
                              label={t("leave.balance.breakdown_carryover")}
                              hours={row.carryover_hours ?? "0"}
                            />
                          )}
                          <BreakdownRow
                            label={t("leave.balance.breakdown_used")}
                            hours={row.used_hours}
                          />
                          <BreakdownRow
                            label={t("leave.balance.breakdown_pending")}
                            hours={row.pending_hours}
                            help={
                              <HelpHint
                                label={t("common.help_for", {
                                  field: t("leave.balance.breakdown_pending"),
                                })}
                              >
                                {t("leave.balance.breakdown_pending_help")}
                              </HelpHint>
                            }
                          />
                          <BreakdownRow
                            total
                            label={t("leave.balance.breakdown_remaining")}
                            hours={row.balance_hours}
                          />
                        </dl>
                      )}
                    {/* LV-11: the years behind the total. Only a ledger-backed
                        balance has any; a hand-keyed one has no service-year
                        structure and shows none rather than an invented one. */}
                    {rated && row.per_service_year !== undefined && row.per_service_year.length > 0 && (
                      <section
                        aria-label={t("leave.balance.per_year_title")}
                        className="mt-3 border-t border-[var(--border-subtle)] pt-2.5 font-sans text-[12.5px]"
                      >
                        <div className="mb-1 text-[var(--fg-muted)]">{t("leave.balance.per_year_title")}</div>
                        <ul className="m-0 list-none space-y-1 p-0 text-[var(--fg-secondary)]">
                          {row.per_service_year.map((year) => (
                            <li key={year.year_index}>
                              {t("leave.balance.per_year_row", {
                                year: year.year_index,
                                date: year.anniversary_date,
                                granted: fmtSpan(year.granted_hours),
                                used: fmtSpan(year.used_hours),
                                carried: fmtSpan(year.carried_hours),
                              })}
                            </li>
                          ))}
                        </ul>
                      </section>
                    )}
                    <div className="mt-3 border-t border-[var(--border-subtle)] pt-2.5 font-serif text-[12.5px] italic text-[var(--fg-muted)]">
                      {t(`leave.balance.status.${row.status}`)}
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>
          {query.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={query.fetchNextPage}
                loading={query.isFetchingNextPage}
              >
                {t("leave.balance.load_more")}
              </Button>
            </div>
          )}
          <BalanceBasisNote />
        </>
      )}
      <UpcomingHolidays />
    </div>
  );
}
